import * as React from 'react';

import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import websiteData = require('diy/util/websiteData');
import HelpIcon from "material-ui/svg-icons/action/help-outline";

import IconButton from "material-ui/IconButton";

import * as features from '../util/features';
import {Notice as PrivacyNotice} from './../privacy/Notice';

export interface IframeMessage {
    type: string;
    imageString?: string;
    language?: string;
}

export interface IframeEditorSaveData {
    imageDataUrl: string;
    payload?: any; // if client needs to send extra data
    isDirty?: boolean; // true means that image has changes
}

export interface IframeEditorDialogProps extends React.Props<IframeEditorDialog> {
    open: boolean;
    imageUrl: string;
    originalImageUrl?: string;
    iframeConfig?: {
        height?: string;
        width?: string;
        cssId?: string;
        name?: string;
    };
    texts?: {
        title: string;
        closeBtn: string;
        saveBtn: string;
        closeDialogTitle: string;
        closeDialogText: string;
        closeDialogConfirm: string;
        closeDialogAbort: string;
        restoreOriginalBtn: string;
        restoreDialogTitle: string;
        restoreDialogText: string;
        restoreDialogConfirm: string;
        restoreDialogAbort: string;
    };
    onClose: () => void;
    onSave: (data: IframeEditorSaveData) => void;
    onHelp?(): void;
}

interface IframeEditorDialogState {
    resultImageUrl: string;
    imageUrl: string;
    editorLoading: boolean;
    editorSaving: boolean;
    showConfirmClose: boolean;
    showConfirmRestore: boolean;
}

export default class IframeEditorDialog extends React.Component<IframeEditorDialogProps, IframeEditorDialogState> {

    static defaultProps = {
        iframeConfig: {
            height: "700",
            width: "100%",
            cssId: "iframe-image-editor"
        },
        texts: {
            title: 'Edit Image',
            closeBtn: 'Close',
            saveBtn: 'Save',
            closeDialogTitle: 'Close editor?',
            closeDialogText: 'Changes will be discarded.',
            closeDialogConfirm: 'OK',
            closeDialogAbort: 'Cancel',
            restoreOriginalBtn: 'Restore Original',
            restoreDialogTitle: 'Restore original?',
            restoreDialogText: 'All changes to the image will be discarded!',
            restoreDialogConfirm: 'OK',
            restoreDialogAbort: 'Cancel'
        }
    };

    /**
     * After modification we save all images as PNG but if the customer uploaded a JPG the original image can't be
     * found anymore. Therefore we need to check if the image is now a PNG or a GIF what could be possible also.
     *
     * @param originalImageUrl
     */
    private static resolveOriginalImageUrl(originalImageUrl: string) {
        let http = new XMLHttpRequest();
        let urlArray = originalImageUrl.split("/");
        http.open(
            'GET',
            '/app/cms/iframedimageseditor/findoriginalimage?imageId=' + urlArray[urlArray.length - 1].split(".")[0],
            false
        );
        http.send();

        if (http.status !== 404 && http.status !== 400) {
            originalImageUrl = JSON.parse(http.response).imageUrl;
        }

        return originalImageUrl;
    }

    constructor(props: IframeEditorDialogProps) {
        super(props);
        this.state = {
            resultImageUrl: "",
            imageUrl: this.props.imageUrl,
            editorLoading: true,
            editorSaving: false,
            showConfirmClose: false,
            showConfirmRestore: false
        };
    }

    componentDidMount(): void {
        window.addEventListener('message', this.onIframeEvent);
    }

    componentWillUnmount() {
        window.removeEventListener('message', this.onIframeEvent);

    }

    render() {
        const styles = this.getStyles();
        const texts = this.props.texts;

        const iframeUrl = features.getPayload<any>('iframedImageEditorConfig').url;
        const iframeUrlWithImage = iframeUrl + '#' + this.props.imageUrl;

        const actions = [
            <FlatButton
                label={texts.closeBtn}
                secondary={true}
                onTouchTap={this.onClose}
                data-test-id="asdk-close-btn"
            />,
            <FlatButton
                label={texts.restoreOriginalBtn}
                secondary={true}
                onTouchTap={this.onRestore}
                data-test-id="asdk-restore-btn"
                disabled={this.state.editorLoading}
            />,
            <RaisedButton
                label={texts.saveBtn}
                primary={true}
                onTouchTap={this.onSave}
                data-test-id="asdk-save-btn"
                disabled={this.state.editorLoading}
            />,
        ];
        if (features('privacyNotice')) {
            actions.unshift(
                <div style={styles.privacyNotice}>
                    <PrivacyNotice />
                </div>
            );
        }

        return (
            <Dialog
                title={texts.title}
                open={this.props.open}
                modal={true}
                actions={actions}
                contentStyle={styles.dialogContent}
                autoScrollBodyContent={true}>
                {
                    this.props.onHelp
                        ? <IconButton onClick={this.props.onHelp} style={styles.helpButton}><HelpIcon/></IconButton>
                        : null
                }
                <div style={styles.editorWrap}>
                    <iframe src={iframeUrlWithImage}
                            id={this.props.iframeConfig.cssId}
                            width={this.props.iframeConfig.width}
                            height={this.props.iframeConfig.height}
                            name={name}
                            style={styles.iframe} />
                </div>
                {
                    this.state.showConfirmClose
                        ?
                        this.renderConfirmation(
                            texts.closeDialogTitle,
                            texts.closeDialogText,
                            texts.closeDialogConfirm,
                            this.onCloseConfirm,
                            texts.closeDialogAbort,
                            this.onCloseDismiss
                        )
                        :
                        null
                }
                {
                    this.state.showConfirmRestore
                        ?
                        this.renderConfirmation(
                            texts.restoreDialogText,
                            texts.closeDialogText,
                            texts.closeDialogConfirm,
                            this.onRestoreConfirm,
                            texts.closeDialogAbort,
                            this.onRestoreDismiss
                        )
                        :
                        null
                }
            </Dialog>
        );
    }

    private sendMessage = (message: IframeMessage) => {
        let iframe: any = document.getElementById(this.props.iframeConfig.cssId);
        iframe.contentWindow.postMessage(message, '*');
    };

    private loadImage = (url: string): Promise<string> => {
        return new Promise((resolve) => {
            let img = document.createElement('img');
            img.setAttribute("style", "display:none");

            img.onload = () => {
                let canvas = document.createElement("canvas");
                let ctx = canvas.getContext("2d");

                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;

                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL());
            };
            img.setAttribute("src", url);
        });
    };

    private renderConfirmation = (
        title: string,
        message: string,
        confirmLabel: string,
        onConfirm: () => void,
        dismissLabel: string,
        onDismiss: () => void
    ) => {
        // TODO maybe extract in a separate component
        return (
            <Dialog
                title={title}
                open={true}
                actions={[
                    <FlatButton
                        label={dismissLabel}
                        secondary={true}
                        onTouchTap={onDismiss}
                    />,
                    <RaisedButton
                        label={confirmLabel}
                        primary={true}
                        onTouchTap={onConfirm}
                    />,
                ]}>
                {message}
            </Dialog>
        );
    };

    private getStyles = () => {
        return {
            dialogContent: {
                width: 1100,
                maxWidth: 1100,
                minWidth: 970
            },
            editorWrap: {
                position: 'relative'
            },
            iframe: {
                border: 0
            },
            helpButton: {
                position: 'absolute',
                top: 12,
                right: 12
            },
            privacyNotice: {
                position: 'absolute',
                fontSize: '11px',
                boxSizing: 'border-box',
                left: 24,
                bottom: 16
            }
        };
    };

    private onIframeEvent = (event: MessageEvent) => {
        if (typeof(event.data.type) === 'undefined') {
            return;
        }

        switch (event.data.type) {
            case 'image-editor-started':
                this.loadImage(this.props.imageUrl).then((image: string) => {
                    this.sendMessage({
                        type: "image-editor-load-image",
                        imageString: image,
                        language: websiteData.getLanguageCode()
                    });
                    this.setState({
                        editorLoading: false
                    } as IframeEditorDialogState);
                });
                break;

            case 'image-editor-image-changed':
                if (this.state.editorSaving) {
                    this.setState({
                        editorSaving: false
                    } as IframeEditorDialogState);

                    this.props.onSave({imageDataUrl: event.data.imageString});
                }
                break;

            default:
                return;
        }
    };

    private onClose = () => {
        this.setState({
            showConfirmClose: true
        } as IframeEditorDialogState);
    };

    private onCloseDismiss = () => {
        this.setState({
            showConfirmClose: false
        } as IframeEditorDialogState);
    };

    private onCloseConfirm = () => {
        this.setState({
            showConfirmClose: false
        } as IframeEditorDialogState);
        this.props.onClose();
    };

    private onRestore = () => {
        this.setState({
            showConfirmRestore: true
        } as IframeEditorDialogState);
    };

    private onRestoreDismiss = () => {
        this.setState({
            showConfirmRestore: false
        } as IframeEditorDialogState);
    };

    private onRestoreConfirm = () => {
        let url = IframeEditorDialog.resolveOriginalImageUrl(this.props.originalImageUrl);

        this.loadImage(url).then(image => {
            this.sendMessage({
                type: "image-editor-load-image",
                imageString: image,
                language: websiteData.getLanguageCode()
            });
        });

        this.setState({
            showConfirmRestore: false,
            imageUrl: this.props.originalImageUrl,
            editorSaving: true
        } as IframeEditorDialogState);
    };

    private onSave = () => {
        this.sendMessage({
            type: "image-editor-update-image"
        });
        this.setState({
            editorSaving: true
        } as IframeEditorDialogState);
    };
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/aviary/IframeImageEditorDialog.tsx
 **/